import { Component, Suspense } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { useLocation } from "react-router-dom";

import { useI18n } from "../contexts/I18nContext";

const reloadPage = () => {
  if (typeof window !== "undefined") window.location.reload();
};

export const RouteLoadingFallback = () => {
  const { t } = useI18n();

  return (
    <div
      className="min-h-[50vh] flex items-center justify-center"
      data-testid="route-chunk-loading"
    >
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span>{t("جاري تحميل الصفحة...", "Loading page...")}</span>
      </div>
    </div>
  );
};

class RouteChunkErrorBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidUpdate(previousProps) {
    if (
      this.state.failed &&
      previousProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (this.state.failed) return this.props.fallback;
    return this.props.children;
  }
}

export const RouteContentBoundary = ({ children, onRetry = reloadPage }) => {
  const location = useLocation();
  const { t } = useI18n();

  const errorFallback = (
    <div
      className="min-h-[50vh] flex items-center justify-center"
      data-testid="route-chunk-error"
      role="alert"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center">
        <h2 className="font-heading text-xl font-bold text-foreground">
          {t("تعذر تحميل الصفحة", "Failed to load this page")}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t(
            "أعد تحميل الصفحة للمحاولة مرة أخرى.",
            "Reload the page to try again.",
          )}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <RefreshCw className="h-4 w-4" />
          {t("إعادة التحميل", "Reload")}
        </button>
      </div>
    </div>
  );

  return (
    <RouteChunkErrorBoundary
      resetKey={`${location.pathname}${location.search}`}
      fallback={errorFallback}
    >
      <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>
    </RouteChunkErrorBoundary>
  );
};
